/**
 * Aligns two single-ROI binary voxel crops onto ONE shared voxel grid so they
 * can be fed to {@link compareContours}, which requires both masks on an
 * identical grid (see its `assertSameGrid`).
 *
 * ROI segmentations are stored as per-ROI signed-distance-field textures
 * CROPPED to each ROI's content bounding box. Two ROIs of the same image
 * therefore share voxel SPACING and ORIENTATION but differ in crop ORIGIN and
 * SIZE. Turning an SDF into a binary crop is an engine-layer concern (it needs
 * the SDF iso-value), so this module takes the already-binarised crops — each
 * tagged with its integer origin on the common image-voxel lattice — and
 * composes them into two masks over their union bounding box.
 *
 * The composition is an EXACT integer-voxel paste: because both crops live on
 * the same lattice (identical spacing + orientation, integer origins), no
 * resampling or interpolation is required, so the operation is deterministic
 * and lossless — a hard requirement for a reproducible clinical metric.
 *
 * Domain layer (CLAUDE.md): pure, no engine/GPU/DOM dependency; distances stay
 * in mm. Unit-testable in isolation from the engine.
 */
import {
    BinaryMask,
    ContourComparisonError,
    MaskGrid,
} from './contourComparisonMetrics';

/**
 * One ROI's binary segmentation, cropped to its content bounds and positioned
 * on a shared integer voxel lattice (the image grid both ROIs derive from).
 */
export interface RoiBinaryCrop {
    /** Crop size in voxels, `[x, y, z]`; non-negative integers. */
    readonly sizeVox: readonly [number, number, number];
    /**
     * Crop origin: the integer voxel coordinate of this crop's voxel (0,0,0) on
     * the shared image-voxel lattice, `[x, y, z]`. May be negative relative to
     * the other crop — only the two crops' RELATIVE offset matters.
     */
    readonly originVox: readonly [number, number, number];
    /** Voxel spacing in mm, `[x, y, z]`; must match the other crop. */
    readonly spacingMm: readonly [number, number, number];
    /**
     * Binary voxels for the crop, length `sizeVox.x * sizeVox.y * sizeVox.z`,
     * indexed `x + sizeX * (y + sizeY * z)`; non-zero = foreground.
     */
    readonly voxels: Uint8Array;
}

/** Reference + test masks on one shared grid, ready for `compareContours`. */
export interface AlignedRoiMasks {
    readonly reference: BinaryMask;
    readonly test: BinaryMask;
}

function assertCropShape(crop: RoiBinaryCrop, label: string): void {
    const [sx, sy, sz] = crop.sizeVox;
    if (
        !Number.isInteger(sx) ||
        !Number.isInteger(sy) ||
        !Number.isInteger(sz) ||
        sx < 0 ||
        sy < 0 ||
        sz < 0
    ) {
        throw new ContourComparisonError(
            `${label} crop size must be non-negative integers: ` +
                `${sx}x${sy}x${sz}.`
        );
    }
    for (let i = 0; i < 3; i += 1) {
        if (!Number.isInteger(crop.originVox[i])) {
            throw new ContourComparisonError(
                `${label} crop origin must be integer voxel coordinates.`
            );
        }
    }
    const expected = sx * sy * sz;
    if (crop.voxels.length !== expected) {
        throw new ContourComparisonError(
            `${label} crop voxel length ${crop.voxels.length} does not match ` +
                `crop volume ${expected}.`
        );
    }
}

function assertSameSpacing(a: RoiBinaryCrop, b: RoiBinaryCrop): void {
    for (let i = 0; i < 3; i += 1) {
        if (a.spacingMm[i] !== b.spacingMm[i]) {
            throw new ContourComparisonError(
                'Crops must share voxel spacing (mm) to be aligned.'
            );
        }
    }
}

interface UnionFrame {
    readonly grid: MaskGrid;
    /** Reference crop's paste offset (origin − unionMin) in voxels. */
    readonly offsetReference: readonly [number, number, number];
    /** Test crop's paste offset (origin − unionMin) in voxels. */
    readonly offsetTest: readonly [number, number, number];
}

/** Bounding box covering both crops on the shared lattice, plus paste offsets. */
function computeUnionFrame(a: RoiBinaryCrop, b: RoiBinaryCrop): UnionFrame {
    const min: [number, number, number] = [0, 0, 0];
    const size: [number, number, number] = [0, 0, 0];
    for (let i = 0; i < 3; i += 1) {
        const aMin = a.originVox[i];
        const bMin = b.originVox[i];
        const aMax = aMin + a.sizeVox[i];
        const bMax = bMin + b.sizeVox[i];
        min[i] = Math.min(aMin, bMin);
        size[i] = Math.max(aMax, bMax) - min[i];
    }
    return {
        grid: {
            sizeX: size[0],
            sizeY: size[1],
            sizeZ: size[2],
            spacingMm: a.spacingMm,
        },
        offsetReference: [
            a.originVox[0] - min[0],
            a.originVox[1] - min[1],
            a.originVox[2] - min[2],
        ],
        offsetTest: [
            b.originVox[0] - min[0],
            b.originVox[1] - min[1],
            b.originVox[2] - min[2],
        ],
    };
}

/** Paste a crop's foreground voxels into a fresh union-grid mask buffer. */
function pasteCrop(
    crop: RoiBinaryCrop,
    grid: MaskGrid,
    offset: readonly [number, number, number]
): Uint8Array {
    const out = new Uint8Array(grid.sizeX * grid.sizeY * grid.sizeZ);
    const [sx, sy, sz] = crop.sizeVox;
    const [ox, oy, oz] = offset;
    const src = crop.voxels;
    let s = 0;
    for (let z = 0; z < sz; z += 1) {
        const uz = z + oz;
        for (let y = 0; y < sy; y += 1) {
            const uy = y + oy;
            let dst = ox + grid.sizeX * (uy + grid.sizeY * uz);
            for (let x = 0; x < sx; x += 1, s += 1, dst += 1) {
                if (src[s] !== 0) {
                    out[dst] = 1;
                }
            }
        }
    }
    return out;
}

/**
 * Compose two binary ROI crops into reference and test masks over their shared
 * union grid. The result is exact (integer paste, no interpolation); both masks
 * share size, spacing, and origin so they satisfy `compareContours`' grid
 * invariant.
 *
 * @throws ContourComparisonError if a crop is malformed (non-integer
 * size/origin, wrong voxel length) or the two crops' spacings differ.
 */
export function alignBinaryCrops(
    reference: RoiBinaryCrop,
    test: RoiBinaryCrop
): AlignedRoiMasks {
    assertCropShape(reference, 'Reference');
    assertCropShape(test, 'Test');
    assertSameSpacing(reference, test);

    const frame = computeUnionFrame(reference, test);
    return {
        reference: {
            grid: frame.grid,
            voxels: pasteCrop(reference, frame.grid, frame.offsetReference),
        },
        test: {
            grid: frame.grid,
            voxels: pasteCrop(test, frame.grid, frame.offsetTest),
        },
    };
}
